import { useCallback, useEffect, useState } from 'react';
import {
	GetCandidatesResponse,
	NPHHSortingRequest,
	PagingConfig,
	PreworkDashboardDetail,
	PreworkCandidateFilter,
	PreworkGroupFilter,
	GetPreworkGroupResponse,
} from '../../../shared/PreWorkServiceTypes';
import { useSnapshot } from 'valtio';
import {
	DashboardDetailState,
	DashboardListState,
	GroupsListState,
	CandidatesBatchState,
	CandidatesListState,
} from './CandidatesList.store';
import { useAuth } from '../../../hooks/useAuth';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
	createPreworkGroup,
	getPreworkDashboardMetrics,
	getPreworkGroups,
	getPreworkCandidates,
} from '../../../service/PreWorkService';
import {
	COUNTRY_CODE_INDIA,
	DEFAULT_PAGE_SIZE,
	PreworkGroupTabs,
} from '../../../shared/Constants';
import { useConfirmationBeforeAction } from '../../../hooks/useConfirmationBeforeAction';
import { useCallApi } from '../../../hooks/useCallApi';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { PREWORKGROUPS } from '../../../shared/Routes';
import { usePreworkGroupId } from '../../../hooks/usePreworkGroupId';
import useQueryParams from '../../../hooks/useQueryParams';
import useUserSession from '../../../hooks/useUserSession';
import { hasPrivilege } from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';

export const useCreateGroupPrework = () => {
	const [candidatesList, setCandidatesList] = useState<GetCandidatesResponse>({
		candidates: [],
		pagination: { totalNoOfRecords: 0 },
	});
	const [groupsList, setGroupsList] = useState<GetPreworkGroupResponse>({
		preworkGroup: [],
		pagination: { totalNoOfRecords: 0 },
	});
	const [dashboardCandidatesList, setDashboardCandidatesList] =
		useState<GetCandidatesResponse>({
			candidates: [],
			pagination: { totalNoOfRecords: 0 },
		});
	const [dashboardDetail, setDashboardDetail] = useState<
		PreworkDashboardDetail | undefined
	>();
	const { navigateWithPreworkBatchIdAndGroupTab } = usePreworkGroupId();
	const queryParams = useQueryParams();
	const groupId = queryParams.get('preworkGroupId');
	const tabKey = queryParams.get('preworkActiveTab');
	const [activeTabKey, setActiveTabKey] = useState(tabKey);
	const { t } = useTranslation();
	const { confirmBeforeAction } = useConfirmationBeforeAction();
	const { callApi } = useCallApi();
	const { srcmId } = useAuth();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const candidatesFilterStateSnap = useSnapshot(CandidatesListState);
	const groupsFilterStateSnap = useSnapshot(GroupsListState);
	const dashboardFilterStateSnap = useSnapshot(DashboardListState);
	const dashboardDetailFilterStateSnap = useSnapshot(DashboardDetailState);
	const candidateBatchSnap = useSnapshot(CandidatesBatchState);
	const selectedBatchId = candidateBatchSnap.selectedBatch?.id;
	const [candidatePaging, setCandidatePaging] = useState<PagingConfig>(() => {
		return { ...candidatesFilterStateSnap.paging } as PagingConfig;
	});
	const [groupPaging, setGroupPaging] = useState<PagingConfig>(() => {
		return { ...groupsFilterStateSnap.paging } as PagingConfig;
	});
	const [dashboardPaging, setDashboardPaging] = useState<PagingConfig>(() => {
		return { ...dashboardFilterStateSnap.paging } as PagingConfig;
	});

	const showPreworkCandidates = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_CANDIDATES,
		loggedInUserPrivileges
	);

	const [candidatesFilter, setCandidatesFilter] =
		useState<PreworkCandidateFilter>({
			showNewCandidatesForGrouping: true,
			countryIds: [COUNTRY_CODE_INDIA],
			batchId: selectedBatchId,
		});
	const [groupsFilter, setGroupsFilter] = useState<PreworkGroupFilter>(() => {
		const sortingRequest: NPHHSortingRequest = {
			sortingFieldName: 'GROUP_ID',
			sortingOrder: 'DESCENDING',
		};
		if (groupsFilterStateSnap.filterBy) {
			return {
				...groupsFilterStateSnap.filterBy,
				batchId: selectedBatchId,
			} as PreworkGroupFilter;
		} else {
			return {
				sortingRequests: [sortingRequest],
				batchId: selectedBatchId,
			} as PreworkGroupFilter;
		}
	});
	const [dashboardFilter, setDashboardFilter] =
		useState<PreworkCandidateFilter>(() => {
			if (dashboardFilterStateSnap.filterBy) {
				return {
					...dashboardFilterStateSnap.filterBy,
					showAllCandidates: true,
					countryIds: [COUNTRY_CODE_INDIA],
					batchId: selectedBatchId,
				} as PreworkCandidateFilter;
			} else {
				return {
					showAllCandidates: true,
					countryIds: [COUNTRY_CODE_INDIA],
					batchId: selectedBatchId,
				} as PreworkCandidateFilter;
			}
		});
	const [dashboardDetailFilter, setDashboardDetailFilter] =
		useState<PreworkCandidateFilter>(() => {
			if (dashboardDetailFilterStateSnap.filterBy) {
				return {
					...dashboardDetailFilterStateSnap.filterBy,
					showPreceptorDashboardCardDetail: true,
					countryIds: [COUNTRY_CODE_INDIA],
					batchId: selectedBatchId,
				} as PreworkCandidateFilter;
			} else {
				return {
					showPreceptorDashboardCardDetail: true,
					countryIds: [COUNTRY_CODE_INDIA],
					batchId: selectedBatchId,
				} as PreworkCandidateFilter;
			}
		});

	useEffect(() => {
		if (candidateBatchSnap) {
			setCandidatesFilter((prevState) => ({
				...prevState,
				batchId: selectedBatchId,
			}));
			setGroupsFilter((prevState) => ({
				...prevState,
				batchId: selectedBatchId,
			}));
			setDashboardFilter((prevState) => ({
				...prevState,
				batchId: selectedBatchId,
			}));
			setDashboardDetailFilter((prevState) => ({
				...prevState,
				batchId: selectedBatchId,
			}));
		}
	}, [candidateBatchSnap, selectedBatchId]);

	const onGroupsPageChange = useCallback(
		(pageNumber: number, pageSize: number) => {
			GroupsListState.paging = { pageNo: pageNumber, pageSize };
			setGroupPaging({ pageNo: pageNumber, pageSize });
		},
		[]
	);

	const onGroupsFilterChange = useCallback(
		(filterParam: PreworkGroupFilter) => {
			GroupsListState.filterBy = { ...filterParam };
			setGroupsFilter(filterParam);
			onGroupsPageChange(1, groupPaging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onGroupsPageChange, groupPaging.pageSize]
	);

	const fetchGroupsList = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			filter: PreworkGroupFilter
		) => {
			if (srcmId && selectedRole && filter.batchId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;

				try {
					const groupsWithoutKey = await getPreworkGroups({
						functionarySrcmId: srcmId,
						pageIndex,
						pageSize,
						preworkGroupFilter: filter,
						selectedRoleCode: selectedRole,
					});

					const groups = {
						...groupsWithoutKey,
						...(groupsWithoutKey.preworkGroup && {
							preworkGroup: groupsWithoutKey.preworkGroup.map((group) => ({
								...group,
								key: group.id,
							})),
						}),
					};

					if (
						groups.pagination.totalNoOfPages &&
						groups.pagination.totalNoOfPages > 0 &&
						pageNumber > groups.pagination.totalNoOfPages
					) {
						onGroupsPageChange(groups.pagination.totalNoOfPages ?? 1, pageSize);
					} else {
						setGroupsList(groups);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, selectedRole, setLoading, onGroupsPageChange, showToastMessage]
	);

	const onDashboardDetailFilterChange = useCallback(
		(filterParam: PreworkCandidateFilter) => {
			DashboardDetailState.filterBy = { ...filterParam };
			setDashboardDetailFilter(filterParam);
		},
		[]
	);

	const onDashboardPageChange = useCallback(
		(pageNumber: number, pageSize: number) => {
			DashboardListState.paging = { pageNo: pageNumber, pageSize };
			setDashboardPaging({ pageNo: pageNumber, pageSize });
		},
		[]
	);

	const onDashboardFilterChange = useCallback(
		(filterParam: PreworkCandidateFilter) => {
			DashboardListState.filterBy = { ...filterParam };
			setDashboardFilter(filterParam);
			onDashboardPageChange(1, dashboardPaging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onDashboardPageChange, dashboardPaging.pageSize]
	);

	const fetchDashboardList = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			filter: PreworkCandidateFilter
		) => {
			if (srcmId && selectedRole && filter.batchId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const candidatesWithoutKey = await getPreworkCandidates({
						functionarySrcmId: srcmId,
						selectedRoleCode: selectedRole,
						pageIndex,
						pageSize,
						candidateFilter: filter,
					});
					const candidates = {
						...candidatesWithoutKey,
						...(candidatesWithoutKey.candidates && {
							candidates: candidatesWithoutKey.candidates.map((candidate) => ({
								...candidate,
								key: candidate.applicationId,
							})),
						}),
					};

					if (
						candidates.pagination.totalNoOfPages &&
						candidates.pagination.totalNoOfPages > 0 &&
						pageNumber > candidates.pagination.totalNoOfPages
					) {
						onDashboardPageChange(
							candidates.pagination.totalNoOfPages ?? 1,
							pageSize
						);
					} else {
						setDashboardCandidatesList(candidates);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, selectedRole, setLoading, onDashboardPageChange, showToastMessage]
	);

	const fetchDashboardCardDetails = useCallback(
		async (filter: PreworkCandidateFilter) => {
			if (srcmId && selectedRole && filter.batchId) {
				setLoading(true);
				try {
					setDashboardDetail(undefined);
					const candidateDashboardCardDetail = await getPreworkDashboardMetrics(
						{
							functionarySrcmId: srcmId,
							candidateFilter: filter,
							selectedRoleCode: selectedRole,
						}
					);
					setDashboardDetail(candidateDashboardCardDetail);
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, selectedRole, setLoading, showToastMessage]
	);

	const onCandidatesPageChange = useCallback(
		(pageNumber: number, pageSize: number) => {
			CandidatesListState.paging = { pageNo: pageNumber, pageSize };
			setCandidatePaging({ pageNo: pageNumber, pageSize });
		},
		[]
	);

	const onCandidateFilterChange = useCallback(
		(filterParam: PreworkCandidateFilter) => {
			CandidatesListState.filterBy = { ...filterParam };
			setCandidatesFilter(filterParam);
			onCandidatesPageChange(1, candidatePaging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onCandidatesPageChange, candidatePaging.pageSize]
	);

	const fetchCandidatesList = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			filter: PreworkCandidateFilter
		) => {
			if (srcmId && selectedRole && filter.batchId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const candidatesWithoutKey = await getPreworkCandidates({
						functionarySrcmId: srcmId,
						selectedRoleCode: selectedRole,
						pageIndex,
						pageSize,
						candidateFilter: filter,
					});
					const candidates = {
						...candidatesWithoutKey,
						...(candidatesWithoutKey.candidates && {
							candidates: candidatesWithoutKey.candidates.map((candidate) => ({
								...candidate,
								key: candidate.applicationId,
							})),
						}),
					};

					if (
						candidates.pagination.totalNoOfPages &&
						candidates.pagination.totalNoOfPages > 0 &&
						pageNumber > candidates.pagination.totalNoOfPages
					) {
						onCandidatesPageChange(
							candidates.pagination.totalNoOfPages ?? 1,
							pageSize
						);
					} else {
						setCandidatesList(candidates);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[srcmId, selectedRole, setLoading, onCandidatesPageChange, showToastMessage]
	);

	const onConfirmCreateGroup = useCallback(
		async (candidateIds: Array<number>) => {
			try {
				await callApi(
					() =>
						createPreworkGroup({
							functionarySrcmId: srcmId as string,
							candidateIds,
							batchId: selectedBatchId,
						}),
					'errorOccurredWhileCreatingGroup'
				);
				Modal.success({
					content: t('createdGroupSuccessfully'),
					afterClose: () => {
						navigateWithPreworkBatchIdAndGroupTab(
							PREWORKGROUPS,
							PreworkGroupTabs.GROUPS_LIST_VIEW
						);
						setActiveTabKey(PreworkGroupTabs.GROUPS_LIST_VIEW);
						void fetchGroupsList(
							groupPaging.pageNo ?? 1,
							groupPaging.pageSize ?? DEFAULT_PAGE_SIZE,
							groupsFilter
						);

						void fetchCandidatesList(
							candidatePaging.pageNo ?? 1,
							candidatePaging.pageSize ?? DEFAULT_PAGE_SIZE,
							candidatesFilter
						);
					},
				});
			} catch (e) {
				// Do Nothing
			}
		},
		[
			callApi,
			srcmId,
			navigateWithPreworkBatchIdAndGroupTab,
			t,
			fetchGroupsList,
			fetchCandidatesList,
			groupPaging,
			groupsFilter,
			candidatePaging,
			candidatesFilter,
			selectedBatchId,
		]
	);

	const onCreateGroupClick = useCallback(
		(candidateIds: Array<number>) => {
			void confirmBeforeAction(
				'confirmUpdateGroup',
				'areYouSureToCreateGroupCandidate',
				() => {
					setCandidatesFilter({
						showNewCandidatesForGrouping: true,
						countryIds: [COUNTRY_CODE_INDIA],
						batchId: selectedBatchId,
					});
					void onConfirmCreateGroup(candidateIds);
				}
			);
		},
		[confirmBeforeAction, onConfirmCreateGroup, selectedBatchId]
	);

	useEffect(() => {
		if (!showPreworkCandidates) return;
		if (activeTabKey === PreworkGroupTabs.CREATE_GROUP || groupId) {
			void fetchCandidatesList(
				candidatePaging.pageNo ?? 1,
				candidatePaging.pageSize ?? DEFAULT_PAGE_SIZE,
				candidatesFilter
			);
		}
	}, [
		activeTabKey,
		candidatePaging,
		candidatesFilter,
		fetchCandidatesList,
		groupId,
		showPreworkCandidates,
	]);

	useEffect(() => {
		if (activeTabKey === PreworkGroupTabs.GROUPS_LIST_VIEW) {
			void fetchGroupsList(
				groupPaging.pageNo ?? 1,
				groupPaging.pageSize ?? DEFAULT_PAGE_SIZE,
				groupsFilter
			);
		}
	}, [activeTabKey, groupPaging, groupsFilter, fetchGroupsList]);

	useEffect(() => {
		if (activeTabKey === PreworkGroupTabs.VIEW_ALL_NEW_CANDIDATES) {
			void fetchDashboardCardDetails(dashboardDetailFilter);
		}
	}, [dashboardDetailFilter, fetchDashboardCardDetails, activeTabKey]);

	useEffect(() => {
		if (activeTabKey === PreworkGroupTabs.VIEW_ALL_NEW_CANDIDATES) {
			void fetchDashboardList(
				dashboardPaging.pageNo ?? 1,
				dashboardPaging.pageSize ?? DEFAULT_PAGE_SIZE,
				dashboardFilter
			);
		}
	}, [activeTabKey, dashboardPaging, dashboardFilter, fetchDashboardList]);

	return {
		candidatesList,
		candidatesFilter,
		onCandidateFilterChange,
		onCandidatesPageChange,
		candidatePaging,
		onCreateGroupClick,
		groupsList,
		groupsFilter,
		onGroupsFilterChange,
		onGroupsPageChange,
		groupPaging,
		dashboardDetail,
		onDashboardDetailFilterChange,
		dashboardCandidatesList,
		dashboardFilter,
		onDashboardFilterChange,
		onDashboardPageChange,
		dashboardPaging,
		activeTabKey,
		setActiveTabKey,
	};
};
